/**
 * Orders Copilot Actions
 * AI-executable actions for orders
 * Uses: store/slice actions (reuses existing actions)
 */

import { useCopilotAction } from '@copilotkit/react-core'
import { useDispatch } from '@spcc/core'
import { ordersActions } from '../store/orders.slice'
import { DEFAULT_ORDERS_FILTER } from '../domain/filter-config'
import type { OrderStatus, OrdersFilter } from '../domain/types'

const ORDER_STATUS_VALUES: OrderStatus[] = ['pending', 'processing', 'delivered', 'cancelled']

function normalizeStatusFilter(status?: string): OrderStatus[] {
  if (!status || status === 'all') return []
  return ORDER_STATUS_VALUES.includes(status as OrderStatus) ? [status as OrderStatus] : []
}

function buildDefaultFilter(): OrdersFilter {
  return {
    ...DEFAULT_ORDERS_FILTER,
    status: [],
    products: [],
    deliveryAddresses: [],
  }
}

/**
 * Register order actions that AI can execute
 */
export const useOrdersCopilotActions = () => {
  const dispatch = useDispatch()

  // Search orders action
  useCopilotAction({
    name: 'searchOrders',
    description: 'Search for orders by order number, product name, or status',
    parameters: [
      {
        name: 'query',
        type: 'string',
        description: 'Search query (order number or product name)',
        required: false,
      },
      {
        name: 'status',
        type: 'string',
        description: 'Filter by status: pending, processing, delivered, cancelled, or all',
        required: false,
      },
    ],
    handler: async ({ query, status }) => {
      if (query) {
        dispatch(ordersActions.searchOrders(query))
      }
      if (status) {
        dispatch(ordersActions.filterByStatus(normalizeStatusFilter(status)))
      }
      return `Searching orders${query ? ` for "${query}"` : ''}${status ? ` with status "${status}"` : ''}`
    },
  })

  // Create order action
  useCopilotAction({
    name: 'createOrder',
    description: 'Create a new cement order for delivery',
    parameters: [
      {
        name: 'productName',
        type: 'string',
        description: 'Name of the cement product',
        required: true,
      },
      {
        name: 'quantity',
        type: 'number',
        description: 'Quantity in bags',
        required: true,
      },
      {
        name: 'deliveryAddress',
        type: 'string',
        description: 'Delivery address',
        required: true,
      },
      {
        name: 'deliveryDate',
        type: 'string',
        description: 'Requested delivery date (YYYY-MM-DD)',
        required: true,
      },
    ],
    handler: async ({ productName, quantity, deliveryAddress, deliveryDate }) => {
      dispatch(ordersActions.createOrder({
        customerId: 'cust-001', // Would come from auth context
        items: [{
          productId: 'prod-1',
          product: { en: productName, ar: productName },
          quantity: { value: quantity, unit: 'bags' },
          unitPrice: { amount: 28, currency: 'SAR' },
        }],
        deliveryDate,
        deliveryAddress,
      }))
      return `Creating order for ${quantity} bags of ${productName} to be delivered to ${deliveryAddress} on ${deliveryDate}`
    },
  })

  // Cancel order action
  useCopilotAction({
    name: 'cancelOrder',
    description: 'Cancel an existing order (only pending or processing orders can be cancelled)',
    parameters: [
      {
        name: 'orderId',
        type: 'string',
        description: 'Order ID or order number to cancel',
        required: true,
      },
      {
        name: 'reason',
        type: 'string',
        description: 'Reason for cancellation',
        required: true,
      },
    ],
    handler: async ({ orderId, reason }) => {
      dispatch(ordersActions.cancelOrder({ orderId, reason }))
      return `Cancelling order ${orderId}. Reason: ${reason}`
    },
  })

  // Get order details action
  useCopilotAction({
    name: 'getOrderDetails',
    description: 'Get detailed information about a specific order',
    parameters: [
      {
        name: 'orderNumber',
        type: 'string',
        description: 'The order number (e.g., ORD-2024-001)',
        required: true,
      },
    ],
    handler: async ({ orderNumber }) => {
      dispatch(ordersActions.searchOrders(orderNumber))
      return `Looking up order ${orderNumber}`
    },
  })

  // Clear filters action
  useCopilotAction({
    name: 'clearOrderFilters',
    description: 'Clear all order filters and show all orders',
    parameters: [],
    handler: async () => {
      dispatch(ordersActions.setFilter(buildDefaultFilter()))
      return 'Cleared all filters, showing all orders'
    },
  })
}
